import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  Form,
  InputGroup,
} from "react-bootstrap";
import Swal from "sweetalert2";
import api from "@/api/api.js";
import {
  LEGAL,
  acceptLegal,
  fetchCompanyLegal,
  fetchLegalStatus,
  openLegalDocument,
} from "@/api/legal.js";

const SIGNATURE_ENDPOINT = (companyId) =>
  `/companies/${companyId}/digitalSignature/latest`;

/** El texto de cada casilla, tal como lo entregó el área legal (tarjeta #12). */
const CHECKBOX = {
  [LEGAL.contract]: {
    before: "He leído y acepto el ",
    title: "Contrato de Prestación de Servicios",
  },
  [LEGAL.protocol]: {
    before: "He leído y acepto el ",
    title: "Protocolo de Gestión de Incidentes de Seguridad",
  },
};

/**
 * Acuse que ve el dueño al terminar de entregar su e.firma, con la bienvenida
 * y el paso siguiente: comprar folios.
 */
const SignatureReceipt = () => {
  const navigate = useNavigate();
  return (
    <div className="text-start">
      <h6 className="fw-bold">
        <i className="bi bi-check-circle-fill text-success me-2" />
        e.firma registrada
      </h6>
      <p>
        Tu e.firma quedó registrada correctamente. A partir de este momento,
        CFD-IPUNTO® queda autorizado a utilizarla exclusivamente para las
        operaciones que tú solicites, conforme al Contrato de Prestación de
        Servicios y al Protocolo de Gestión de Incidentes de Seguridad que
        aceptaste. Conserva esta confirmación como comprobante de tu entrega.
      </p>
      <p className="fw-semibold">
        ¡Bienvenido a CFD-IPUNTO®! Tu cuenta ya está lista. Elige el paquete de
        folios que mejor se ajuste a tu operación y comienza a emitir tus CFDI
        en minutos.
      </p>
      <Button variant="primary" onClick={() => navigate("/buy_package")}>
        Ver paquetes
      </Button>
    </div>
  );
};

/**
 * Carga de la e.firma.
 *
 * Antes de entregarla, el dueño de la cuenta acepta el contrato y el
 * protocolo (tarjeta #4): con `asOwner` se le piden aquí, en dos casillas, y
 * se registran al subir. El contador (`asOwner={false}`) no acepta por nadie:
 * si al titular le falta, el cargador se queda deshabilitado con la razón.
 * El servidor lo comprueba de nuevo y rechaza la carga sin aceptación.
 */
const SignatureUpload = ({ companyId, onUploadSuccess, asOwner = true }) => {
  const [mode, setMode] = useState("zip");
  const [file, setFile] = useState(null);
  const [cerFile, setCerFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Lo que le falta aceptar al dueño (null mientras se pregunta).
  const [missing, setMissing] = useState(null);
  const [checked, setChecked] = useState({});
  const [receipt, setReceipt] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = asOwner ? fetchLegalStatus() : fetchCompanyLegal(companyId);
    load
      .then((data) => {
        if (!cancelled) setMissing(data.missingForSignature ?? []);
      })
      .catch(() => {
        // Si no se pudo preguntar, el servidor decide al subir.
        if (!cancelled) setMissing([]);
      });
    return () => {
      cancelled = true;
    };
  }, [asOwner, companyId]);

  const pending = missing ?? [];
  // Mientras no se sabe qué falta, no se deja subir.
  const legalReady =
    missing !== null &&
    (asOwner
      ? pending.every((doc) => checked[doc.document])
      : pending.length === 0);

  const onDrop = useCallback((accepted) => {
    if (accepted.length > 0) setFile(accepted[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
    },
    maxFiles: 1,
    multiple: false,
  });

  const reset = () => {
    setFile(null);
    setCerFile(null);
    setKeyFile(null);
    setPassword("");
    setShowPassword(false);
  };

  const canSubmit =
    legalReady &&
    !!password &&
    !submitting &&
    (mode === "zip" ? !!file : !!cerFile && !!keyFile);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const formData = new FormData();
    if (mode === "zip") {
      formData.append("file", file);
    } else {
      formData.append("cer", cerFile);
      formData.append("key", keyFile);
    }
    formData.append("password", password);

    setSubmitting(true);
    try {
      if (asOwner && pending.length > 0) {
        await acceptLegal(pending.map((doc) => doc.document));
        setMissing([]);
      }
      const response = await api.post(SIGNATURE_ENDPOINT(companyId), formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.status === 200 || response.status === 201) {
        if (asOwner) {
          reset();
          setReceipt(true);
          onUploadSuccess?.();
          return;
        }
        Swal.fire({
          icon: "success",
          title: "Firma cargada correctamente",
          timer: 2000,
          showConfirmButton: false,
        });
        reset();
        onUploadSuccess?.();
      }
    } catch (err) {
      console.error("Error al subir la firma:", err);
      Swal.fire({
        icon: "error",
        title: "No se pudo subir la firma",
        text:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Verifica los archivos y la contraseña.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (receipt) {
    return (
      <Card className="mb-3">
        <Card.Body>
          <SignatureReceipt />
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="h6 mb-3">
          Firma electrónica (FIEL / CSD)
        </Card.Title>

        <ButtonGroup className="mb-3" size="sm">
          <Button
            type="button"
            variant={mode === "zip" ? "primary" : "outline-primary"}
            onClick={() => setMode("zip")}
          >
            Archivo .zip
          </Button>
          <Button
            type="button"
            variant={mode === "separate" ? "primary" : "outline-primary"}
            onClick={() => setMode("separate")}
          >
            .cer y .key por separado
          </Button>
        </ButtonGroup>

        {asOwner && pending.length > 0 && (
          <div className="mb-3">
            {pending.map((doc) => (
              <Form.Check
                key={doc.document}
                id={`accept-${doc.document}`}
                className="mb-2"
                checked={!!checked[doc.document]}
                onChange={(e) =>
                  setChecked((prev) => ({
                    ...prev,
                    [doc.document]: e.target.checked,
                  }))
                }
                label={
                  <>
                    {CHECKBOX[doc.document]?.before ?? "He leído y acepto el "}
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        openLegalDocument(doc.document);
                      }}
                    >
                      {CHECKBOX[doc.document]?.title ?? doc.title}
                    </a>{" "}
                    de CFD-IPUNTO®.
                  </>
                }
              />
            ))}
            {!legalReady && (
              <small className="text-muted d-block">
                Acepta el contrato y el protocolo para continuar.
              </small>
            )}
          </div>
        )}
        {asOwner && missing !== null && pending.length === 0 && (
          <small className="text-muted d-block mb-3">
            <i className="bi bi-shield-check me-1" />
            Ya aceptaste el Contrato de Prestación de Servicios y el Protocolo
            de Gestión de Incidentes de Seguridad.
          </small>
        )}
        {!asOwner && pending.length > 0 && (
          <Alert variant="warning" className="py-2">
            El titular de la cuenta todavía no acepta{" "}
            {pending.map((doc) => `el ${doc.title}`).join(" ni ")}. Debe
            aceptarlos desde su cuenta antes de que se pueda entregar su
            e.firma.
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <fieldset disabled={!legalReady} aria-label="Archivos de la e.firma">
            {mode === "zip" ? (
              <div
                {...getRootProps()}
                className="dropzone mb-3"
                style={{
                  border: "2px dashed #ccc",
                  padding: "16px",
                  textAlign: "center",
                  cursor: "pointer",
                  borderRadius: "4px",
                }}
              >
                <input {...getInputProps()} />
                {file ? (
                  <p className="mb-0">
                    <i className="bi bi-file-earmark-zip me-1" />
                    {file.name}
                  </p>
                ) : isDragActive ? (
                  <p className="mb-0">Suelta el .zip aquí...</p>
                ) : (
                  <p className="mb-0">
                    Arrastra el .zip con .cer y .key, o haz click para
                    seleccionar.
                  </p>
                )}
              </div>
            ) : (
              <>
                <Form.Group className="mb-3" controlId="signatureCer">
                  <Form.Label>Certificado (.cer)</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".cer"
                    onChange={(e) => setCerFile(e.target.files?.[0] || null)}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="signatureKey">
                  <Form.Label>Llave privada (.key)</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".key"
                    onChange={(e) => setKeyFile(e.target.files?.[0] || null)}
                  />
                </Form.Group>
              </>
            )}

            <Form.Group className="mb-3" controlId="signaturePassword">
              <Form.Label>Contraseña de la clave privada</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  required
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                >
                  <i
                    className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}
                  />
                </Button>
              </InputGroup>
            </Form.Group>

            <div className="d-flex gap-2">
              <Button type="submit" variant="primary" disabled={!canSubmit}>
                {submitting ? "Subiendo..." : "Subir firma"}
              </Button>
              {(file || cerFile || keyFile || password) && !submitting && (
                <Button type="button" variant="link" onClick={reset}>
                  Limpiar
                </Button>
              )}
            </div>
          </fieldset>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default SignatureUpload;
